import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import {
  deletePurchase,
  deletePurchaseReturn,
  getAllPurchase,
  createReturnPurchase,
  getSinglePurchase,
} from "../../services/services";
import Loading from "../../components/loading";
import moment from "moment";
import { toastError, toastSuccess } from "../../utils";
import PartyCardDetail from "../party/partyCardDetail";
import SearchFilter from "./SearchFilter";
import AddReturn from "../returns/addReturn";
import Pagination from "../../components/Pagination";
import { toast } from "react-toastify";
import { confirmDelete } from "../../components/confirmDelete";

const Purchase = () => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [selectedPurchaseData, setSelectedPurchaseData] = useState(null);
  const [partyLedgerData, setPartyLedgerData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);

  const [purchaseId, setPurchaseId] = useState(null);
  /* FETCH */

  const fetchPurchases = async (payloadOverride) => {
    setLoading(true);
    try {
      const payload = payloadOverride || {
        query: { page: currentPage, limit },
      };

      const response = await getAllPurchase(payload);
      if (response.data.status) {
        const list = response.data.response.purchases || [];
        setPurchases(list);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(response.data.currentPage || 1);

        if (selectedPurchase) {
          const updated = list.find((p) => p._id === selectedPurchase._id);
          setSelectedPurchase(updated || null);
        }
      } else {
        setPurchases([]);
        toastError("Failed to fetch purchases");
      }
    } catch (error) {
      setPurchases([]);
      toastError("Failed to fetch purchases");
    } finally {
      setLoading(false);
    }
  };

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#0b2b6d",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#0b2b6d",
        color: "#ffffff",
        // fontWeight: "600",
        // fontSize: "14px",
        textTransform: "uppercase",
      },
    },
  };
  useEffect(() => {
    fetchPurchases();
  }, [currentPage, limit]);

  const handleSearch = (payload) => {
    payload.query = { ...payload.query, page: 1, limit };

    if (payload.query.searchBy?.dateRange?.length === 2) {
      const [from, to] = payload.query.searchBy.dateRange;
      payload.query.searchBy.date = `${from},${to}`;
      delete payload.query.searchBy.dateRange;
    }

    setCurrentPage(1);
    fetchPurchases(payload);
  };

  const handleDelete = async (id, row) => {
    const label =
      row.referenceType === "purchaseReturn" ? "Purchase Return" : "Purchase";

    const name = row.vendor?.name || "Vendor";
    const invoice = row.invoiceNumber || row._id.slice(-6);

    const isConfirm = await confirmDelete(`${label} - ${name} (${invoice})`);

    if (!isConfirm) return;
    try {
      const res =
        row.referenceType === "purchaseReturn"
          ? await deletePurchaseReturn(id)
          : await deletePurchase(id);

      toastSuccess(res.data.message);
      fetchPurchases({ query: { page: currentPage, limit } });
      if (selectedPurchase?._id === id) setSelectedPurchase(null);
    } catch (error) {
      toastError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Delete failed",
      );
    }
  };

  const openReturnModal = (purchase) => {
    if (!purchase?.items?.length) {
      toastError("No items available to return");
      return;
    }
    setSelectedPurchaseData(purchase);
    setReturnModalOpen(true);
  };

  const handleReturnSubmit = async (data) => {
    try {
      const payload = {
        purchaseId: data.purchaseId,
        reason: data.reason,
        items: data.items.map((item) => ({
          productId: item.productId,
          batchCode: item.batchCode,
          quantity: Number(item.quantity),
          itemName: item.itemName,
          purchasePrice: item.purchasePrice,
          unit: item.unit,
        })),
      };

      await createReturnPurchase(payload);

      fetchPurchases({ query: { page: currentPage, limit } });
      setReturnModalOpen(false);
      setSelectedPurchaseData(null);
      toastSuccess("Purchase return processed successfully");
    } catch (error) {
      console.error("Return submit error:", error);
      toastError(
        error.response?.data?.error || "Failed to create purchase return",
      );
    }
  };

  const handleSelectPurchase = async (purchase) => {
    try {
      const res = await getSinglePurchase(purchase._id);

      if (res.data.status) {
        const { singlePurchase, returns } = res.data.response;

        const ledgerLike = {
          ...singlePurchase,
          party: singlePurchase.vendor,
          date: singlePurchase.purchaseDate,
          referenceId: singlePurchase._id,
          returns: returns || [],
        };

        setSelectedPurchase(ledgerLike);
        setPurchaseId(singlePurchase.invoiceNumber);
      }
    } catch (error) {
      console.error("Failed to fetch purchase", error);
      toast.error("Failed to load purchase details");
    }
  };

  /* DATA TABLE COLUMNS */
  const columns = useMemo(
    () => [
      {
        name: "P.No",
        selector: (row) => row.invoiceNumber,
        sortable: true,
      },
      {
        name: "Vendor",
        selector: (row) => row.vendor?.name || "-",
      },
      //       {
      //   name: "Item",
      //   selector: (row) =>
      //     row.items?.length
      //       ? row.items.map((item) => item.itemName).join(", ")
      //       : "No Item",
      // },
      {
        name: "Item",
        cell: (row) => {
          if (!row.items?.length) return "No Item";

          const names = row.items.map(
            (i) => i.itemName || i.productId?.name || "Unnamed Item",
          );
          const visibleItems = names.slice(0, 2).join(", ");
          const remaining = names.length - 2;

          const displayText =
            remaining > 0 ? `${visibleItems} +${remaining} more` : visibleItems;

          return (
            <div
              style={{
                maxWidth: "220px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={names.join(", ")}
            >
              {displayText}
            </div>
          );
        },
        grow: 0,
        //  grow: 2,
      },
      {
        name: "Total (₹)",
        selector: (row) =>
          typeof row.totalAmount === "number"
            ? Math.abs(row.totalAmount).toFixed(2)
            : "-",
      },
      // {
      //   name: "Discount (₹)",
      //   selector: (row) =>
      //     typeof row.discountAmount === "number"
      //       ? Math.abs(row.discountAmount).toFixed(2)
      //       : "-",
      // },
      {
        name: "Due (₹)",
        selector: (row) =>
          typeof row.dueAmount === "number"
            ? Math.abs(row.dueAmount).toFixed(2)
            : "-",
      },
      {
        name: "Purchase Date",
        width: "140px",
        selector: (row) => moment(row.purchaseDate).format("DD MMM, YYYY"),
      },
      {
        name: "Action",
        center: true,
        cell: (row) => (
          <div className="d-flex gap-2">
            <i
              className="bi bi-eye btn btn-light shadow-sm px-2"
              title="View"
              onClick={(e) => {
                e.stopPropagation();
                handleSelectPurchase(row);
              }}
            />
            <i
              className="bi bi-arrow-repeat btn btn-light shadow-sm text-primary border"
              title="Return"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPurchase(null);
                openReturnModal(row);
              }}
            />
            <i
              className="bi bi-trash btn btn-light shadow-sm text-danger border"
              title="Delete"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row._id, row);
              }}
            />
          </div>
        ),
      },
    ],
    [partyLedgerData],
  );

  return (
    <>
      <div id="main">
        <div className="d-flex align-items-center justify-content-between mb-4 gap-3 flex-column flex-md-row">
          <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="custom-title mb-0 text-start">Manage Purchases</h4>
            {/* mobile */}
            <Link to="/purchases/create" className="d-block d-md-none ">
              <button className="btn btn-primary p-2 text-nowrap">
                Create Purchase
              </button>
            </Link>
          </div>
          <div className="d-flex align-items-center gap-2 ">
            <SearchFilter
              onSearch={handleSearch}
              showItemName={false}
              showProductFilters={false}
            />
            <Link to="/purchases/create" className="d-none d-md-block">
              <button className="btn btn-primary p-2 text-nowrap ">
                Create Purchase
              </button>
            </Link>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0 rounded">
            {loading ? (
              <Loading />
            ) : (
              <DataTable
                columns={columns}
                data={purchases}
                customStyles={customStyles}
                highlightOnHover
                pointerOnHover
                onRowClicked={handleSelectPurchase}
                noDataComponent={
                  <div className="py-4 text-muted text-center">
                    <i className="bi bi-inbox fs-1 mb-2 d-block"></i>
                    No purchases records found
                  </div>
                }
              />
            )}
          </div>
        </div>
        {purchases.length > 0 && (
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            limit={limit}
            onPageChange={setCurrentPage}
            onLimitChange={setLimit}
          />
        )}
        {selectedPurchase && (
          <>
            <div
              className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25"
              style={{ zIndex: 1049 }}
              onClick={() => setSelectedPurchase(null)}
            />
            <PartyCardDetail
              invoiceNumber={purchaseId}
              ledgerEntry={selectedPurchase}
              onClose={() => setSelectedPurchase(null)}
            />
          </>
        )}

        {returnModalOpen && (
          <AddReturn
            isOpen={returnModalOpen}
            onClose={() => {
              setReturnModalOpen(false);
              setSelectedPurchaseData(null);
            }}
            onSubmit={handleReturnSubmit}
            purchaseData={selectedPurchaseData}
            returnType="purchase"
          />
        )}
      </div>
    </>
  );
};

export default headerLayoutWrapper(Purchase);
